import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Share2, Check } from 'lucide-react';
import { sharePayload, shareFallbackText, SHARE_URL } from '@shared/ui/share-text.js';
import { formatEventDateShort } from '@shared/ui/event-date.js';

/**
 * Pass the event on, from the moment someone books it.
 *
 * Three routes, in order, because no single one exists everywhere:
 *
 *   1. The native share sheet, via the Web Share API. Android, ChromeOS,
 *      Windows and Safari have it.
 *   2. The clipboard. Desktop Firefox has no navigator.share at all, and
 *      neither does Chrome on macOS or Linux, so this is not an edge case —
 *      it is what a large share of desktop visitors get, and it has to feel
 *      finished rather than broken.
 *   3. The URL, revealed as selectable text. Reached when the clipboard is
 *      unavailable, which is anything served over plain http. There is always
 *      a way out.
 */
export default function ShareButton({ eventDate }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  /*
   * Two keys rather than one with an interpolated date: i18next cannot drop a
   * clause when the value is missing, and "The Monday Festival,  — take a
   * look" is worse than a sentence that never mentions the date.
   */
  const date = formatEventDateShort(eventDate);
  const message = date ? t('share.messageWithDate', { date }) : t('share.message');

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share(sharePayload({ message }));
        return;
      } catch (err) {
        /*
         * Dismissing the sheet rejects with AbortError. Falling through to the
         * clipboard here would silently overwrite whatever the person had
         * copied, as a reward for changing their mind.
         */
        if (err?.name === 'AbortError') return;
        console.warn('[share] native share failed, falling back', err);
      }
    }

    try {
      await navigator.clipboard.writeText(shareFallbackText({ message }));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('[share] clipboard unavailable', err);
      setRevealed(true);
    }
  };

  return (
    <div>
      <p className="label-tag text-white/40 text-[10px] mb-2.5">{t('share.title')}</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={share}
          className="inline-flex items-center gap-2 bg-white/5 border border-white/15 text-white
                     font-heading font-bold uppercase text-sm px-4 py-2.5 rounded-lg
                     hover:border-brand/40 transition"
        >
          {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
          {copied ? t('share.copied') : t('share.button')}
        </button>
      </div>

      {revealed && (
        <p className="text-white/40 text-xs font-body mt-2.5 select-all break-all">{SHARE_URL}</p>
      )}
    </div>
  );
}
